import React, { useEffect, useState } from "react";
import api, { apiErrorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Award, ClipboardList, ListOrdered } from "lucide-react";
import { downloadFile } from "@/lib/download";
import { FileSpreadsheet } from "lucide-react";

export default function RankingPage() {
  const [periodes, setPeriodes] = useState([]);
  const [kelompokList, setKelompokList] = useState([]);
  const [kelompokFilter, setKelompokFilter] = useState("");
  const [periodeId, setPeriodeId] = useState("");
  const [data, setData] = useState(null);
  const [rankingAkhir, setRankingAkhir] = useState(null);
  const [kelompokAkhirId, setKelompokAkhirId] = useState("");
  const [globalSettings, setGlobalSettings] = useState(null);
  const [deskripsi, setDeskripsi] = useState(null);

  useEffect(() => {
    api.get("/admin/periode").then((r) => setPeriodes(r.data));
    api.get("/admin/kelompok").then((r) => setKelompokList(r.data));
    api.get("/admin/settings").then((r) => setGlobalSettings(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (!periodeId) { setData(null); return; }
    api.get(`/admin/periode/${periodeId}/hasil`).then((r) => setData(r.data)).catch((e) => toast.error(apiErrorMsg(e)));
  }, [periodeId]);

  // Fetch rekapitulasi ranking akhir bila kelompokAkhirId dipilih
  useEffect(() => {
    if (!kelompokAkhirId) { setRankingAkhir(null); setDeskripsi(null); return; }
    api.get(`/admin/kelompok/${kelompokAkhirId}/ranking-akhir`)
      .then((r) => setRankingAkhir(r.data))
      .catch((e) => { setRankingAkhir(null); toast.error(apiErrorMsg(e)); });
    api.get(`/admin/kelompok/${kelompokAkhirId}/deskripsi-penilaian`)
      .then((r) => setDeskripsi(r.data))
      .catch(() => setDeskripsi(null));
  }, [kelompokAkhirId]);

  const filteredPeriodes = kelompokFilter ? periodes.filter((p) => p.kelompok_id === kelompokFilter) : periodes;
  const manualRanks = data?.ranks || {};
  const sortedByNilai = data ? [...data.hasil].sort((a, b) => (b.nilai_unsur?.nilai_sikap || 0) - (a.nilai_unsur?.nilai_sikap || 0)) : [];
  const scaleActive = data?.settings?.scale_to_range;

  // Derived: laporan bulanan/mingguan (sort by nilai_sikap desc, ranking = KET)
  const laporanRows = sortedByNilai.map((h, i) => ({
    no: i + 1,
    nama: h.siswa?.nama || "",
    nosis: h.siswa?.nosis || "",
    rohani: h.nilai_unsur?.mental_rohani || 0,
    ideologi: h.nilai_unsur?.mental_ideologi || 0,
    kejuangan: h.nilai_unsur?.mental_kejuangan || 0,
    rata: h.nilai_unsur?.nilai_sikap || 0,
    ket: i + 1,
  }));
  const periodeInfo = data?.periode || {};
  const kelompokInfo = kelompokList.find((k) => k.id === periodeInfo.kelompok_id) || null;
  const pendidikan = kelompokInfo?.pendidikan || data?.settings?.pendidikan || data?.settings?.kop_l2 || "";
  const jenisLabel = periodeInfo.jenis === "bulanan" ? "Bulan" : "Minggu";
  const periodeStr = periodeInfo.nomor
    ? `${jenisLabel} ke-${periodeInfo.nomor}${periodeInfo.bulan || periodeInfo.tahun ? ` (${periodeInfo.bulan || ""} ${periodeInfo.tahun || ""})` : ""}`
    : "";

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-brass-600 font-semibold mb-1">Ranking</div>
        <h1 className="font-heading text-3xl font-bold text-navy-900">Ranking Serdik</h1>
        <p className="text-slate-500 mt-1 text-sm">Perbandingan ranking Pengasuh vs ranking otomatis berdasar Nilai Sikku.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-4 mb-4 flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-auto">
          <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Filter Kelompok</label>
          <select value={kelompokFilter} onChange={(e) => { setKelompokFilter(e.target.value); setPeriodeId(""); }} data-testid="ranking-kelompok-filter" className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white w-full sm:w-auto sm:min-w-[220px]">
            <option value="">Semua kelompok</option>
            {kelompokList.map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
          </select>
        </div>
        <div className="w-full sm:w-auto">
          <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Periode</label>
          <select value={periodeId} onChange={(e) => setPeriodeId(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white w-full sm:w-auto sm:min-w-[280px]">
            <option value="">-- Pilih periode --</option>
            {filteredPeriodes.map((p) => <option key={p.id} value={p.id}>{p.jenis === "mingguan" ? "Minggu" : "Bulan"} ke-{p.nomor} • {p.bulan} {p.tahun}{p.kelompok_nama ? ` • ${p.kelompok_nama}` : ""}</option>)}
          </select>
        </div>
        {data && (
          <>
            <button onClick={() => downloadFile(`/admin/periode/${periodeId}/export/nilai-gabungan`, "Ranking.xlsx")} className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" /> Excel Nilai Gabungan</button>
            <button
              onClick={() => downloadFile(
                `/admin/periode/${periodeId}/export/laporan-bulming`,
                `Laporan${periodeInfo.jenis === "bulanan" ? "Bulanan" : "Mingguan"}_${(kelompokInfo?.nama || "").replace(/\s+/g, "_")}.xlsx`,
              )}
              data-testid="ranking-download-laporan-bulming"
              className="bg-navy-900 hover:bg-navy-800 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"
            ><FileSpreadsheet className="w-4 h-4" /> Excel Laporan {periodeInfo.jenis === "bulanan" ? "Bulanan" : "Mingguan"}</button>
          </>
        )}
      </div>

      {data && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Ranking dari Pengasuh */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="ranking-pengasuh">
            <div className="bg-navy-900 text-white p-3">
              <h3 className="font-heading font-bold flex items-center gap-2"><Trophy className="w-4 h-4 text-brass-400" /> Ranking Pengasuh</h3>
              <div className="text-xs text-white/60 mt-0.5">Input manual oleh Pengasuh</div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-700 text-xs uppercase">
                  <th className="p-2 w-12 text-center">Rk</th>
                  <th className="p-2 text-left">Nama</th>
                  <th className="p-2 w-20 text-center">Sikku</th>
                  <th className="p-2 w-24 text-center">Nilai Akhir</th>
                </tr>
              </thead>
              <tbody>
                {data.hasil.map((h) => ({ ...h, rk: manualRanks[h.siswa.id] })).filter((h) => h.rk).sort((a, b) => a.rk - b.rk).map((h) => (
                  <tr key={h.siswa.id} className="border-t border-slate-100">
                    <td className="p-2 text-center">
                      <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-bold font-mono text-xs ${h.rk === 1 ? "bg-brass-500 text-white" : h.rk === 2 ? "bg-slate-400 text-white" : h.rk === 3 ? "bg-amber-700 text-white" : "bg-slate-200 text-slate-700"}`}>{h.rk}</span>
                    </td>
                    <td className="p-2 font-medium text-navy-900">{h.siswa.nama}<div className="text-[10px] text-slate-400 font-mono">{h.siswa.nosis}</div></td>
                    <td className="p-2 text-center font-mono text-xs">{(h.nilai_unsur?.nilai_sikap || 0).toFixed(2)}</td>
                    <td className="p-2 text-center font-mono font-bold text-navy-900">{(h.nilai_unsur?.nilai_akhir ?? h.nilai_unsur?.nilai_sikap ?? 0).toFixed(2)}</td>
                  </tr>
                ))}
                {Object.keys(manualRanks).length === 0 && <tr><td colSpan="4" className="p-4 md:p-8 text-center text-slate-400">Ranking belum diisi Pengasuh.</td></tr>}
              </tbody>
            </table>
          </div>

          {/* Ranking otomatis */}
          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="ranking-auto">
            <div className="bg-brass-500 text-navy-900 p-3">
              <h3 className="font-heading font-bold flex items-center gap-2"><Award className="w-4 h-4" /> Ranking Otomatis</h3>
              <div className="text-xs text-navy-900/70 mt-0.5">Berdasar Nilai Sikku gabungan (Sosio + Obs)</div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-700 text-xs uppercase">
                  <th className="p-2 w-12 text-center">Rk</th>
                  <th className="p-2 text-left">Nama</th>
                  <th className="p-2 w-20 text-center">Sikku</th>
                  <th className="p-2 w-24 text-center">Nilai Akhir</th>
                </tr>
              </thead>
              <tbody>
                {sortedByNilai.map((h, i) => (
                  <tr key={h.siswa.id} className="border-t border-slate-100">
                    <td className="p-2 text-center">
                      <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-bold font-mono text-xs ${i === 0 ? "bg-brass-500 text-white" : i === 1 ? "bg-slate-400 text-white" : i === 2 ? "bg-amber-700 text-white" : "bg-slate-200 text-slate-700"}`}>{i + 1}</span>
                    </td>
                    <td className="p-2 font-medium text-navy-900">{h.siswa.nama}<div className="text-[10px] text-slate-400 font-mono">{h.siswa.nosis}</div></td>
                    <td className="p-2 text-center font-mono text-xs">{(h.nilai_unsur?.nilai_sikap || 0).toFixed(2)}</td>
                    <td className="p-2 text-center font-mono font-bold text-navy-900">{(h.nilai_unsur?.nilai_akhir ?? h.nilai_unsur?.nilai_sikap ?? 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {data && scaleActive && (
        <div className="mt-4 text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded-sm p-3">
          <strong>Skala Linear Aktif:</strong> Nilai Akhir di-mapping ke rentang [<span className="font-mono">{data.settings.nilai_min}</span> – <span className="font-mono">{data.settings.nilai_max}</span>] berdasar Ranking Pengasuh. Untuk mengubah, buka Setting → Skala Nilai Akhir.
        </div>
      )}

      {/* === Laporan Perkembangan Nilai Sikap & Perilaku Serdik (Bulanan/Mingguan) === */}
      {data && (
        <div className="mt-8 bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="ranking-laporan-bulming">
          <div className="bg-slate-100 border-b border-slate-200 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-brass-500" />
              <div>
                <h3 className="font-heading font-bold text-navy-900 text-sm">
                  Laporan Perkembangan Nilai Sikap &amp; Perilaku Serdik ({periodeInfo.jenis === "bulanan" ? "Bulanan" : "Mingguan"})
                </h3>
                <div className="text-[11px] text-slate-500">Format persis Lap Bulming — untuk cetak ke atasan.</div>
              </div>
            </div>
          </div>

          {/* Preview KOP (kiri atas, center di area kop) + Judul (tanpa underline) */}
          <div className="px-6 py-4 border-b border-slate-200">
            <div className="inline-block text-center">
              <div className="text-sm font-bold uppercase text-navy-900 leading-tight">{data.settings.kop_l1}</div>
              <div className="text-sm font-bold uppercase text-navy-900 leading-tight border-b border-navy-900 pb-1">{data.settings.kop_l2}</div>
            </div>
            <div className="text-base font-bold text-navy-900 mt-6 text-center">
              LAPORAN PERKEMBANGAN NILAI SIKAP DAN PERILAKU SERDIK
            </div>
          </div>

          {/* Info Pendidikan & Periode */}
          <div className="px-6 py-3 text-sm border-b border-slate-200 space-y-1">
            <div className="flex">
              <span className="w-40 text-slate-600">Pendidikan</span>
              <span className="mr-2">:</span>
              <span className="font-medium text-navy-900" data-testid="lapbul-pendidikan">{pendidikan || "—"}</span>
            </div>
            <div className="flex">
              <span className="w-40 text-slate-600">Periode Penilaian</span>
              <span className="mr-2">:</span>
              <span className="font-medium text-navy-900" data-testid="lapbul-periode">{periodeStr}</span>
            </div>
          </div>

          {/* Tabel */}
          <div className="px-6 py-4">
            <table className="w-full text-sm border border-slate-400" data-testid="lapbul-table">
              <thead>
                <tr className="bg-slate-100 text-navy-900">
                  <th rowSpan="2" className="border border-slate-400 p-2 w-12 text-center align-middle">NO</th>
                  <th rowSpan="2" className="border border-slate-400 p-2 text-center align-middle">NAMA</th>
                  <th rowSpan="2" className="border border-slate-400 p-2 w-32 text-center align-middle">NOSIS/<br />NO AK/<br />NIM</th>
                  <th colSpan="3" className="border border-slate-400 p-2 text-center">UNSUR PENILAIAN</th>
                  <th rowSpan="2" className="border border-slate-400 p-2 w-28 text-center align-middle">RATA-RATA NILAI</th>
                  <th rowSpan="2" className="border border-slate-400 p-2 w-16 text-center align-middle">KET</th>
                </tr>
                <tr className="bg-slate-100 text-navy-900 text-xs">
                  <th className="border border-slate-400 p-2 text-center">MENTAL ROHANI</th>
                  <th className="border border-slate-400 p-2 text-center">MENTAL IDEOLOGI</th>
                  <th className="border border-slate-400 p-2 text-center">MENTAL KEJUANGAN</th>
                </tr>
                <tr className="bg-white text-[10px] text-slate-500 font-mono">
                  <td className="border border-slate-400 p-1 text-center">1</td>
                  <td className="border border-slate-400 p-1 text-center">2</td>
                  <td className="border border-slate-400 p-1 text-center">3</td>
                  <td className="border border-slate-400 p-1 text-center">4</td>
                  <td className="border border-slate-400 p-1 text-center">5</td>
                  <td className="border border-slate-400 p-1 text-center">6</td>
                  <td className="border border-slate-400 p-1 text-center">7</td>
                  <td className="border border-slate-400 p-1 text-center">8</td>
                </tr>
              </thead>
              <tbody>
                {laporanRows.map((row) => (
                  <tr key={row.no} className="border border-slate-400" data-testid={`lapbul-row-${row.no}`}>
                    <td className="border border-slate-400 p-2 text-center font-mono">{row.no}</td>
                    <td className="border border-slate-400 p-2 font-medium text-navy-900">{row.nama}</td>
                    <td className="border border-slate-400 p-2 text-center font-mono text-xs">{row.nosis}</td>
                    <td className="border border-slate-400 p-2 text-center font-mono">{row.rohani.toFixed(2)}</td>
                    <td className="border border-slate-400 p-2 text-center font-mono">{row.ideologi.toFixed(2)}</td>
                    <td className="border border-slate-400 p-2 text-center font-mono">{row.kejuangan.toFixed(2)}</td>
                    <td className="border border-slate-400 p-2 text-center font-mono font-bold text-navy-900">{row.rata.toFixed(2)}</td>
                    <td className="border border-slate-400 p-2 text-center font-bold">
                      <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono text-xs ${row.ket === 1 ? "bg-brass-500 text-white" : row.ket === 2 ? "bg-slate-400 text-white" : row.ket === 3 ? "bg-amber-700 text-white" : "bg-slate-200 text-slate-700"}`}>{row.ket}</span>
                    </td>
                  </tr>
                ))}
                {laporanRows.length === 0 && (
                  <tr><td colSpan="8" className="p-6 text-center text-slate-400 border border-slate-400">Belum ada data hasil untuk periode ini.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* TTD block preview */}
          <div className="px-6 py-6 flex justify-end">
            <div className="text-center text-sm max-w-sm">
              <div>{data.settings.lokasi}, {periodeInfo.bulan} {periodeInfo.tahun}</div>
              <div>{data.settings.jabatan_penandatangan},</div>
              <div className="h-16"></div>
              <div className="font-bold text-navy-900">{data.settings.nama_komandan}</div>
              <div>{data.settings.pangkat_komandan} NRP {data.settings.nrp_komandan}</div>
            </div>
          </div>
        </div>
      )}

      {/* === Laporan Ranking Akhir Kelompok (Rekapitulasi antar-periode) === */}
      <div className="mt-8 bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="ranking-akhir-section">
        <div className="bg-slate-100 border-b border-slate-200 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ListOrdered className="w-4 h-4 text-brass-500" />
            <div>
              <h3 className="font-heading font-bold text-navy-900 text-sm">Laporan Ranking Akhir Kelompok</h3>
              <div className="text-[11px] text-slate-500">Rekapitulasi rata-rata nilai per Serdik dari seluruh periode dalam kelompok — format persis Lap Rangking.</div>
            </div>
          </div>
          <div className="flex items-end gap-3">
            <div className="w-full sm:w-auto">
              <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Pilih Kelompok</label>
              <select
                value={kelompokAkhirId}
                onChange={(e) => setKelompokAkhirId(e.target.value)}
                data-testid="ranking-akhir-kelompok-select"
                className="px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white w-full sm:w-auto sm:min-w-[220px]"
              >
                <option value="">-- Pilih kelompok --</option>
                {kelompokList.map((k) => <option key={k.id} value={k.id}>{k.nama}</option>)}
              </select>
            </div>
            {rankingAkhir && rankingAkhir.periodes?.length > 0 && (
              <button
                onClick={() => downloadFile(
                  `/admin/kelompok/${kelompokAkhirId}/export/laporan-ranking-akhir`,
                  `LaporanRankingAkhir_${(rankingAkhir.kelompok?.nama || "").replace(/\s+/g, "_")}.xlsx`,
                )}
                data-testid="ranking-akhir-download"
                className="bg-navy-900 hover:bg-navy-800 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"
              ><FileSpreadsheet className="w-4 h-4" /> Excel Ranking Akhir</button>
            )}
          </div>
        </div>

        {rankingAkhir && (
          <>
            {/* KOP */}
            <div className="px-6 py-4 border-b border-slate-200">
              <div className="inline-block text-center">
                <div className="text-sm font-bold uppercase text-navy-900 leading-tight">{globalSettings?.kop_l1 || data?.settings?.kop_l1 || ""}</div>
                <div className="text-sm font-bold uppercase text-navy-900 leading-tight border-b border-navy-900 pb-1">{globalSettings?.kop_l2 || data?.settings?.kop_l2 || ""}</div>
              </div>
              <div className="text-base font-bold text-navy-900 mt-6 text-center" data-testid="ranking-akhir-title">
                REKAPITULASI NILAI SIKU {(rankingAkhir.kelompok?.pendidikan || "").toUpperCase()}
              </div>
            </div>

            {/* Info Pendidikan & Jumlah Periode */}
            <div className="px-6 py-3 text-sm border-b border-slate-200 space-y-1">
              <div className="flex">
                <span className="w-40 text-slate-600">Pendidikan</span>
                <span className="mr-2">:</span>
                <span className="font-medium text-navy-900" data-testid="ranking-akhir-pendidikan">
                  {rankingAkhir.kelompok?.pendidikan || "—"}
                </span>
              </div>
              <div className="flex">
                <span className="w-40 text-slate-600">Jumlah {(rankingAkhir.jenis_label || "BULAN").toLowerCase() === "minggu" ? "Minggu" : "Bulan"}</span>
                <span className="mr-2">:</span>
                <span className="font-medium text-navy-900" data-testid="ranking-akhir-jumlah-periode">
                  {rankingAkhir.periodes?.length || 0} {(rankingAkhir.jenis_label || "BULAN").toLowerCase()}
                </span>
              </div>
            </div>

            {/* Tabel */}
            <div className="px-6 py-4 overflow-x-auto">
              {rankingAkhir.periodes.length === 0 ? (
                <div className="p-6 text-center text-slate-400 border border-slate-300 rounded-sm">
                  Kelompok ini belum memiliki periode. Buat jadwal pengisian dulu.
                </div>
              ) : (
                <table className="w-full text-sm border border-slate-400 min-w-[720px]" data-testid="ranking-akhir-table">
                  <thead>
                    <tr className="bg-slate-100 text-navy-900">
                      <th rowSpan="2" className="border border-slate-400 p-2 w-12 text-center align-middle">NO</th>
                      <th rowSpan="2" className="border border-slate-400 p-2 text-center align-middle">NAMA</th>
                      <th rowSpan="2" className="border border-slate-400 p-2 w-28 text-center align-middle">NOSIS/<br />NO AK/<br />NIM</th>
                      <th colSpan={rankingAkhir.periodes.length} className="border border-slate-400 p-2 text-center">
                        {rankingAkhir.jenis_label || "BULAN"}
                      </th>
                      <th rowSpan="2" className="border border-slate-400 p-2 w-28 text-center align-middle">RATA-RATA NILAI</th>
                      <th rowSpan="2" className="border border-slate-400 p-2 w-20 text-center align-middle">RANKING</th>
                    </tr>
                    <tr className="bg-slate-100 text-navy-900">
                      {rankingAkhir.periodes.map((p) => (
                        <th key={p.id} className="border border-slate-400 p-2 text-center text-xs">{p.nomor}</th>
                      ))}
                    </tr>
                    <tr className="bg-white text-[10px] text-slate-500 font-mono">
                      <td className="border border-slate-400 p-1 text-center">1</td>
                      <td className="border border-slate-400 p-1 text-center">2</td>
                      <td className="border border-slate-400 p-1 text-center">3</td>
                      {rankingAkhir.periodes.map((_, i) => (
                        <td key={i} className="border border-slate-400 p-1 text-center">{4 + i}</td>
                      ))}
                      <td className="border border-slate-400 p-1 text-center">{4 + rankingAkhir.periodes.length}</td>
                      <td className="border border-slate-400 p-1 text-center">{5 + rankingAkhir.periodes.length}</td>
                    </tr>
                  </thead>
                  <tbody>
                    {[...(rankingAkhir.siswa_rows || [])].sort((a, b) => (a.ranking || 999) - (b.ranking || 999)).map((row, i) => (
                      <tr key={row.siswa.id} data-testid={`ranking-akhir-row-${i + 1}`}>
                        <td className="border border-slate-400 p-2 text-center font-mono">{i + 1}</td>
                        <td className="border border-slate-400 p-2 font-medium text-navy-900">{row.siswa.nama}</td>
                        <td className="border border-slate-400 p-2 text-center font-mono text-xs">{row.siswa.nosis}</td>
                        {row.values.map((v, j) => (
                          <td key={j} className="border border-slate-400 p-2 text-center font-mono">{(v || 0).toFixed(2)}</td>
                        ))}
                        <td className="border border-slate-400 p-2 text-center font-mono font-bold text-navy-900">{(row.rata || 0).toFixed(2)}</td>
                        <td className="border border-slate-400 p-2 text-center font-bold">
                          <span className={`inline-flex w-7 h-7 rounded-full items-center justify-center font-mono text-xs ${row.ranking === 1 ? "bg-brass-500 text-white" : row.ranking === 2 ? "bg-slate-400 text-white" : row.ranking === 3 ? "bg-amber-700 text-white" : "bg-slate-200 text-slate-700"}`}>{row.ranking}</span>
                        </td>
                      </tr>
                    ))}
                    {(rankingAkhir.siswa_rows || []).length === 0 && (
                      <tr><td colSpan={5 + rankingAkhir.periodes.length} className="p-6 text-center text-slate-400 border border-slate-400">Belum ada Serdik pada kelompok ini.</td></tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
        {!rankingAkhir && (
          <div className="px-6 py-8 text-center text-slate-400 text-sm">
            Pilih kelompok di atas untuk melihat rekapitulasi ranking akhir.
          </div>
        )}
      </div>

      {/* === Deskripsi Penilaian Sikap & Perilaku per Serdik === */}
      {deskripsi && deskripsi.siswa_list?.length > 0 && (
        <div className="mt-8 bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="deskripsi-section">
          <div className="bg-slate-100 border-b border-slate-200 px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <ClipboardList className="w-4 h-4 text-brass-500" />
              <div>
                <h3 className="font-heading font-bold text-navy-900 text-sm">Deskripsi Penilaian Sikap &amp; Perilaku per Serdik</h3>
                <div className="text-[11px] text-slate-500">Narasi hasil pengamatan mengikuti format resmi (Aspek Rohani, Idiologi, Kejuangan).</div>
              </div>
            </div>
            <button
              onClick={() => downloadFile(
                `/admin/kelompok/${kelompokAkhirId}/export/deskripsi-penilaian`,
                `DeskripsiPenilaian_${(deskripsi.kelompok?.nama || "").replace(/\s+/g, "_")}.xlsx`,
              )}
              data-testid="deskripsi-download"
              className="bg-navy-900 hover:bg-navy-800 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"
            ><FileSpreadsheet className="w-4 h-4" /> Excel Deskripsi</button>
          </div>

          <div className="divide-y divide-slate-200">
            {deskripsi.siswa_list.map((entry, i) => (
              <div key={entry.siswa.id} className="px-6 py-6" data-testid={`deskripsi-entry-${i + 1}`}>
                {/* Mini KOP + Judul per Serdik */}
                <div className="mb-4">
                  <div className="inline-block text-center">
                    <div className="text-sm font-bold uppercase text-navy-900 leading-tight">{globalSettings?.kop_l1 || ""}</div>
                    <div className="text-sm font-bold uppercase text-navy-900 leading-tight border-b border-navy-900 pb-1">{globalSettings?.kop_l2 || ""}</div>
                  </div>
                  <div className="text-center text-base font-bold text-navy-900 mt-4">
                    DISKRIPSI PENILAIAN SIKAP DAN PERILAKU
                  </div>
                </div>
                {/* Info Serdik */}
                <div className="text-sm space-y-1 mb-4 pl-2">
                  <div className="flex"><span className="w-36 text-slate-600">Pendidikan</span><span className="mr-2">:</span><span className="font-medium text-navy-900">{deskripsi.pendidikan}</span></div>
                  <div className="flex"><span className="w-36 text-slate-600">Nama Serdik</span><span className="mr-2">:</span><span className="font-medium text-navy-900" data-testid={`deskripsi-nama-${i + 1}`}>{entry.siswa.nama}</span></div>
                  <div className="flex"><span className="w-36 text-slate-600">Nosis</span><span className="mr-2">:</span><span className="font-medium text-navy-900 font-mono">{entry.siswa.nosis}</span></div>
                </div>
                {/* Paragraf Deskripsi */}
                <div className="space-y-3 text-sm text-slate-800 leading-relaxed text-justify pl-2 pr-2">
                  <p data-testid={`deskripsi-a-${i + 1}`}>{entry.deskripsi.a}</p>
                  <p data-testid={`deskripsi-b-${i + 1}`}>{entry.deskripsi.b}</p>
                  <p data-testid={`deskripsi-c-${i + 1}`}>{entry.deskripsi.c}</p>
                </div>
                {/* Kecil: nilai per aspek */}
                <div className="mt-3 flex gap-4 text-[11px] font-mono text-slate-500 pl-2">
                  <span>Rohani: <b className="text-navy-900">{(entry.avg_rohani || 0).toFixed(2)}</b></span>
                  <span>Idiologi: <b className="text-navy-900">{(entry.avg_ideologi || 0).toFixed(2)}</b></span>
                  <span>Kejuangan: <b className="text-navy-900">{(entry.avg_kejuangan || 0).toFixed(2)}</b></span>
                  <span>Rata-rata: <b className="text-navy-900">{(entry.rata || 0).toFixed(2)}</b></span>
                </div>
                {/* TTD block */}
                <div className="mt-6 flex justify-end">
                  <div className="text-center text-sm max-w-sm">
                    <div>{globalSettings?.lokasi}, {globalSettings?.bulan} {globalSettings?.tahun}</div>
                    <div>{globalSettings?.jabatan_penandatangan},</div>
                    <div className="h-16"></div>
                    <div className="font-bold text-navy-900">{globalSettings?.nama_komandan}</div>
                    <div>{globalSettings?.pangkat_komandan}</div>
                    <div>NRP {globalSettings?.nrp_komandan}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
